import { fetchWithTimeout } from "../utils/fetchWithTimeout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

/**
 * API service for lead form operations
 */
export class LeadFormAPI {
  /**
   * Get all lead forms with optional date filtering
   * @param {Object} params - Query parameters
   * @param {string} params.from_date - Start date filter (YYYY-MM-DD)
   * @param {string} params.to_date - End date filter (YYYY-MM-DD)
   * @returns {Promise<Object>} API response
   */
  static async getLeadForms(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.from_date) {
        queryParams.append('from_date', params.from_date);
      }
      if (params.to_date) {
        queryParams.append('to_date', params.to_date);
      }
      
      const url = `${BACKEND_URL}/lead/getLeadForms${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetchWithTimeout(
        url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        30000
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching lead forms:', error);
      throw error;
    }
  }
  
  /**
   * Submit a lead form
   * @param {Object} leadData - Lead form data
   * @returns {Promise<Object>} API response
   */
  static async submitLeadForm(leadData) {
    try {
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/lead/submitLeadForm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(leadData),
        },
        30000
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error submitting lead form:', error);
      throw error;
    }
  }
  
  /**
   * Get all detection results with optional date filtering
   * @param {Object} params - Query parameters
   * @param {string} params.from_date - Start date filter (YYYY-MM-DD)
   * @param {string} params.to_date - End date filter (YYYY-MM-DD)
   * @returns {Promise<Object>} API response
   */
  static async getDetectionResults(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.from_date) {
        queryParams.append('from_date', params.from_date);
      }
      if (params.to_date) {
        queryParams.append('to_date', params.to_date);
      }
      
      const url = `${BACKEND_URL}/face/getDetectionResults${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetchWithTimeout(
        url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        30000
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching detection results:', error);
      throw error;
    }
  }

  /**
   * Approve or Reject a lead form (echo only for now)
   * @param {Object} payload
   * @param {string} payload.leadId - Lead ID
   * @param {boolean} payload.approved - true to accept, false to reject
   * @returns {Promise<Object>} API response
   */
  static async approveLead({ leadId, approved }) {
    try {
      const body = { LeadID: leadId, Approved: approved };
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/lead/approveLead`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
        30000
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error approving lead form:', error);
      throw error;
    }
  }

  /**
   * List registered users
   * @returns {Promise<Object>} API response
   */
  static async getRegisteredUsers() {
    try {
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/user/registeredUsers`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        },
        30000
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching registered users:', error);
      throw error;
    }
  }
  /**
   * Get all contact forms with optional date filtering
   * @param {Object} params - Query parameters
   * @param {string} params.from_date - Start date filter (YYYY-MM-DD)
   * @param {string} params.to_date - End date filter (YYYY-MM-DD)
   * @returns {Promise<Object>} API response
   */
  static async getContactForms(params = {}) {
    try {
      const queryParams = new URLSearchParams();
     
      if (params.from_date) {
        queryParams.append('from_date', params.from_date);
      }
      if (params.to_date) {
        queryParams.append('to_date', params.to_date);
      }
     
      const url = `${BACKEND_URL}/contact/getContactForms${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
     
      const response = await fetchWithTimeout(
        url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        30000
      );
     
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
     
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching contact forms:', error);
      throw error;
    }
  }	
}

export default LeadFormAPI;
